/**
 * VCoinCelebrationController — fetches the coach's V-coin wallet and, if
 * there are coins they haven't seen minted yet, plays the full-takeover
 * VCoinCelebration. After the coach collects, it posts /vcoins/celebrated
 * so the moment never replays.
 *
 * Mounted once inside MainDashboardWrapper, so the celebration fires on
 * whatever dashboard page the coach lands on after a coin is earned —
 * the webhook may mint coins while they're offline; this is the replay.
 *
 * Fails completely silently: a rewards hiccup must never affect the
 * dashboard.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import config from '../../../config';
import { getBearerToken } from '../../../util/cookie';
import { createInstance } from '../../../util/sdkLoader';
import VCoinCelebration from './VCoinCelebration';

const VCoinCelebrationController = props => {
  const { currentUser } = props;
  const [pending, setPending] = useState(null); // null = not fetched
  const [balance, setBalance] = useState(0);
  const fetchedRef = useRef(false);

  const userId = currentUser && currentUser.id && currentUser.id.uuid;

  useEffect(() => {
    if (!userId || fetchedRef.current) return;
    fetchedRef.current = true;

    let cancelled = false;
    const run = async () => {
      try {
        const sdk = createInstance({
          transitVerbose: config.sdk.transitVerbose,
          clientId: config.sdk.clientId,
          secure: config.usingSSL,
        });
        const res = await axios.post(
          `${config.subscriptionServiceUrl}vcoins/wallet`,
          {},
          {
            headers: {
              'content-type': 'application/json',
              Authorization: await getBearerToken(sdk),
            },
          }
        );
        if (cancelled) return;
        const { wallet, uncelebrated } = res.data || {};
        if (wallet && Array.isArray(uncelebrated) && uncelebrated.length > 0) {
          setBalance(wallet.balance || 0);
          setPending(uncelebrated);
        }
      } catch (e) {
        // Silent — no wallet yet, service unreachable, etc.
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleDismiss = async keys => {
    setPending(null);
    try {
      const sdk = createInstance({
        transitVerbose: config.sdk.transitVerbose,
        clientId: config.sdk.clientId,
        secure: config.usingSSL,
      });
      await axios.post(
        `${config.subscriptionServiceUrl}vcoins/celebrated`,
        { keys },
        {
          headers: {
            'content-type': 'application/json',
            Authorization: await getBearerToken(sdk),
          },
        }
      );
    } catch (e) {
      // Worst case the celebration replays next visit — acceptable.
    }
  };

  if (!pending || pending.length === 0) return null;

  return <VCoinCelebration events={pending} balance={balance} onDismiss={handleDismiss} />;
};

export default VCoinCelebrationController;
